<template>
  <!-- Фильтр раздела главной — тот же, что в каталоге (Catalog.vue):
       строки марка / модель / модификация, «+» добавляет строку,
       условия строк объединяются по ИЛИ. Выдача обновляется автоматически -->
  <div class="st-filters" role="search" :aria-label="label">
    <div
      v-for="(row, index) in rows"
      :key="index"
      class="catalog-filter-row mb-3"
    >
      <!-- Колонка марки: при появлении «+» селект сужается, остальные колонки не сдвигаются -->
      <div class="catalog-brand-col">
        <div
          class="form-group form-floating catalog-select"
          :class="isHinted(index, 'brand') && 'catalog-select_hint'"
        >
          <select
            class="form-control form-select"
            v-model="row.brand"
            @change="onBrandChange(row)"
          >
            <option value="">Любая</option>
            <option v-for="brand in brands" :key="brand" :value="brand">
              {{ brand }}
            </option>
          </select>
          <label>Марка</label>
        </div>

        <button
          v-if="index === rows.length - 1 && row.brand"
          class="catalog-btn"
          type="button"
          title="Добавить марку"
          @click="addRow"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              fill="currentColor"
              d="M21 11h-8V3h-2v8H3v2h8v8h2v-8h8z"
            ></path>
          </svg>
        </button>
      </div>

      <div
        class="form-group form-floating catalog-select"
        :class="isHinted(index, 'model') && 'catalog-select_hint'"
        @click="!row.brand && flashHint(index, 'brand')"
      >
        <select
          class="form-control form-select"
          v-model="row.model"
          :disabled="!row.brand"
          @change="onModelChange(row)"
        >
          <option value="">Любая</option>
          <option v-for="model in modelsFor(row)" :key="model" :value="model">
            {{ model }}
          </option>
        </select>
        <label>Модель</label>
      </div>

      <!-- Колонка модификации: «✕» всегда в одной строке с селектом -->
      <div class="catalog-mod-col">
        <div
          class="form-group form-floating catalog-select"
          @click="!row.model && flashHint(index, row.brand ? 'model' : 'brand')"
        >
          <select
            class="form-control form-select"
            v-model="row.modification"
            :disabled="!row.model"
          >
            <option value="">Любая</option>
            <option
              v-for="mod in modificationsFor(row)"
              :key="mod"
              :value="mod"
            >
              {{ mod }}
            </option>
          </select>
          <label>Модификация</label>
        </div>

        <button
          v-if="rows.length > 1 || row.brand"
          class="catalog-btn"
          type="button"
          title="Удалить"
          @click="removeRow(index)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              fill="currentColor"
              d="m20.707 4.707-1.414-1.414L12 10.586 4.707 3.293 3.293 4.707 10.586 12l-7.293 7.293 1.414 1.414L12 13.414l7.293 7.293 1.414-1.414L13.414 12z"
            ></path>
          </svg>
        </button>
      </div>
    </div>

    <div v-if="hasActiveFilter" class="catalog-actions">
      <button class="st-btn st-btn--link" type="button" @click="reset">
        Сбросить
      </button>
    </div>
  </div>
</template>

<script>
import "@/views/catalog/style.css";

const emptyRow = () => ({ brand: "", model: "", modification: "" });

export default {
  name: "CatalogFilter",
  props: {
    // сочетания марка / модель / модификация раздела (только то, что есть в базе)
    combos: { type: Array, default: () => [] },
    label: { type: String, default: "Фильтр раздела" },
  },
  // change — строки с выбранной маркой в формате rows для API
  emits: ["change"],
  data() {
    return {
      rows: [emptyRow()],
      // подсветка незаполненного селекта при клике по заблокированному
      hint: { index: null, field: null },
      hintTimer: null,
      // последний отправленный фильтр — чтобы не дёргать выдачу без изменений
      applied: "[]",
    };
  },
  computed: {
    brands() {
      return [...new Set(this.combos.map((combo) => combo.brand))]
        .filter((brand) => brand)
        .sort((a, b) => a.localeCompare(b, "ru"));
    },
    hasActiveFilter() {
      return this.rows.some((row) => row.brand);
    },
  },
  watch: {
    rows: {
      deep: true,
      handler() {
        const rows = this.rows
          .filter((row) => row.brand)
          .map((row) => ({
            brand: row.brand,
            model: row.model,
            modification: row.modification,
          }));
        const key = JSON.stringify(rows);
        if (key === this.applied) return;
        this.applied = key;
        this.$emit("change", rows);
      },
    },
  },
  beforeUnmount() {
    clearTimeout(this.hintTimer);
  },
  methods: {
    modelsFor(row) {
      return [
        ...new Set(
          this.combos
            .filter((combo) => combo.brand === row.brand)
            .map((combo) => combo.model)
        ),
      ]
        .filter((model) => model)
        .sort((a, b) => a.localeCompare(b, "ru"));
    },
    modificationsFor(row) {
      return [
        ...new Set(
          this.combos
            .filter(
              (combo) => combo.brand === row.brand && combo.model === row.model
            )
            .map((combo) => combo.modification)
        ),
      ]
        .filter((mod) => mod)
        .sort((a, b) => a.localeCompare(b, "ru"));
    },
    onBrandChange(row) {
      row.model = "";
      row.modification = "";
    },
    onModelChange(row) {
      row.modification = "";
    },
    addRow() {
      this.rows.push(emptyRow());
    },
    removeRow(index) {
      this.rows.splice(index, 1);
      if (this.rows.length === 0) this.rows.push(emptyRow());
    },
    reset() {
      this.rows = [emptyRow()];
    },
    isHinted(index, field) {
      return this.hint.index === index && this.hint.field === field;
    },
    flashHint(index, field) {
      clearTimeout(this.hintTimer);
      this.hint = { index, field };
      this.hintTimer = setTimeout(() => {
        this.hint = { index: null, field: null };
      }, 2000);
    },
  },
};
</script>
